import { Flex, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSwitchInput } from 'components/common/form/switch';
import { CommonTableHoC } from 'components/common/table';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachine, IMachineFeatureFlags } from 'lib_ts/interfaces/i-machine';
import { useMemo } from 'react';
import useMiscStore from 'stores/use-misc-store';

interface IMachineFeatureFlagRow {
  _id: keyof IMachineFeatureFlags;
  label: string;
  description?: string;
  warning?: string;
}

const MACHINE_FEATURE_FLAG_MAP: {
  [key in keyof IMachineFeatureFlags]: IMachineFeatureFlagRow;
} = {
  // we have since changed our user-facing language around this feature from Jitter Mode to Deception Mode
  jitter_mode: {
    _id: 'jitter_mode',
    label: 'Deception Mode Input',
    description:
      'Enable to show users the Deception Mode input in the Machine Settings dialog.',
  },
  hawkeye: {
    _id: 'hawkeye',
    label: 'Hawkeye',
    description:
      'Enable to show users the "Hawkeye" option when selecting their tracking device.',
  },
  legacy_hit_model: {
    _id: 'legacy_hit_model',
    label: 'Legacy Hit Model',
    description:
      'Enable to use the legacy model to calculate hit results for this machine.',
  },
  liveboard: {
    _id: 'liveboard',
    label: 'Liveboard',
    description:
      'Enable to allow users to connect a Liveboard to this machine.',
  },
};

const MACHINE_FEATURE_FLAGS: IMachineFeatureFlagRow[] = Object.values(
  MACHINE_FEATURE_FLAG_MAP
).sort((a, b) => StringHelper.safeSort(a.label, b.label));

interface IProps {
  machine: Partial<IMachine>;
  updateMetadata: (model: Partial<IMachine>) => void;
  isNew?: boolean;
}

// ! deprecate this in favor of the new feature flags system
export const MachineFeatureFlagsTab = (props: IProps) => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const updating = useMachinesPortalStore((s) => s.updating);

  const columns = useMemo<ITypedTableColumn<IMachineFeatureFlagRow>[]>(
    () => [
      {
        label: 'Feature',
        key: '_feature',
        formatFn: (b) => b.label,
      },
      {
        label: 'Enabled',
        key: '_enabled',
        formatFn: (b) => (
          <CommonSwitchInput
            id={`${b._id}-available-switch`}
            checked={props.machine.feature_flags?.[b._id] ?? false}
            disabled={updating}
            onCheckedChange={(v) =>
              props.updateMetadata({
                feature_flags: {
                  ...props.machine.feature_flags,
                  [b._id]: v,
                },
              })
            }
          />
        ),
      },
      {
        label: 'Description',
        key: '_description',
        formatFn: (b) => (
          // always show the wrapped description
          <Flex direction="column" gap={RADIX.FLEX.GAP.SM} py="1">
            {b.description && (
              <Text style={{ width: '100%', whiteSpace: 'pre-wrap' }}>
                {b.description}
              </Text>
            )}
            {b.warning && (
              <Text
                style={{
                  width: '100%',
                  whiteSpace: 'pre-wrap',
                  fontStyle: 'italic',
                }}
              >
                {b.warning}
              </Text>
            )}
          </Flex>
        ),
      },
    ],
    [props.machine.feature_flags, updating]
  );

  return (
    <ErrorBoundary identifier="MachineFeatureFlagsTab">
      <CommonTableHoC
        dataTestID="MachineFeatureFlagsTable"
        identifier="MachineFeatureFlagsTable"
        data-testid="MachineFeatureFlagsTable"
        displayColumns={columns}
        enableListener={!anyOpenDialogs}
        displayData={MACHINE_FEATURE_FLAGS}
      />
    </ErrorBoundary>
  );
};
